// Currency helpers — infer per-account currency and format money values.
//
// Priority for currency resolution:
//   1. account.currency (explicit user choice, model default "INR")
//   2. broker default (Exness/XM/MT4/MT5/prop-firms → USD; Binance → USDT; Indian brokers → INR)
//   3. "INR" as final fallback
//
// Use `formatMoney(value, currency)` everywhere P&L is displayed. Never
// hardcode ₹ / $ — different accounts denominate in different currencies.

const BROKER_CURRENCY = {
  // MT4/MT5 / prop / global CFD → USD
  exness: "USD", metatrader: "USD", xm: "USD",
  ftmo: "USD", icmarkets: "USD",
  vantage: "USD", vintage: "USD",  // "vintage" is a common misspelling of Vantage
  // Crypto → USDT (Binance) or USD (others)
  binance: "USDT", coinbase: "USD", kraken: "USD",
  // Indian brokers → INR
  zerodha: "INR", angel: "INR", angelone: "INR", upstox: "INR",
  groww: "INR", fyers: "INR", dhan: "INR",
  // Delta Exchange India offers USD-margined perps
  delta: "USD",
};

const CURRENCY_LOCALE = {
  INR: "en-IN",
  USD: "en-US",
  EUR: "en-DE",
  GBP: "en-GB",
  JPY: "ja-JP",
  AUD: "en-AU",
  CAD: "en-CA",
  USDT: "en-US",
};

export const SUPPORTED_CURRENCIES = ["INR", "USD", "EUR", "GBP", "JPY", "AUD", "CAD", "USDT"];

// Default currency for a known broker id (Exness/XM/etc. → USD, Zerodha/Angel/etc. → INR).
// Returns null for unknown/"auto"/"generic" so the caller can decide not to nudge.
export function brokerDefaultCurrency(brokerId) {
  if (!brokerId) return null;
  const b = String(brokerId).toLowerCase().trim();
  if (b === "auto" || b === "generic") return null;
  return BROKER_CURRENCY[b] || null;
}

export function accountCurrency(account) {
  if (!account) return "INR";
  if (account.currency) return String(account.currency).toUpperCase();
  const b = String(account.broker || "").toLowerCase().trim();
  return BROKER_CURRENCY[b] || "INR";
}

export function currencySymbol(currency = "INR") {
  const cur = String(currency).toUpperCase();
  if (cur === "USDT") return "USDT ";
  try {
    const parts = new Intl.NumberFormat(CURRENCY_LOCALE[cur] || "en-US",
      { style: "currency", currency: cur })
      .formatToParts(0);
    return parts.find(p => p.type === "currency")?.value || cur;
  } catch {
    return `${cur} `;
  }
}

// Format a numeric P&L / amount using the given currency.
// Options:
//   sign: true adds a leading "+" for positive values (loss uses "-" naturally).
//   short: true rounds to integer (no decimals) — useful for compact cards.
export function formatMoney(amount, currency = "INR", { sign = false, short = false, compact = false } = {}) {
  const cur = String(currency).toUpperCase();
  const locale = CURRENCY_LOCALE[cur] || "en-US";
  const val = Number(amount) || 0;
  const signStr = sign && val > 0 ? "+" : "";
  const opts = compact
    ? { notation: "compact", maximumFractionDigits: 1 }
    : short
    ? { maximumFractionDigits: 0 }
    : { minimumFractionDigits: 2, maximumFractionDigits: 2 };

  // USDT isn't a real ISO currency — render as suffix like "1,234.56 USDT"
  if (cur === "USDT") {
    return `${signStr}${val.toLocaleString(locale, opts)} USDT`;
  }
  try {
    const s = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: cur,
      ...opts,
    }).format(val);
    return `${signStr}${s}`;
  } catch {
    // Unknown currency code — fall back to suffix rendering
    return `${signStr}${val.toLocaleString(locale, opts)} ${cur}`;
  }
}

// Convenience for accounts: `moneyFor(account, 1234)` → "$1,234.00" or "₹1,234.00"
export function moneyFor(account, amount, opts) {
  return formatMoney(amount, accountCurrency(account), opts);
}
